import { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { upsertLoopsContact, type LoopsContactInput } from "@/lib/loops.functions";
import { trackFormStart, trackFormStep, trackFormSubmit } from "@/lib/analytics/form-tracking";

/** A single-select question step. */
export type ChoiceStep = {
  type: "choice";
  /** Loops property key the answer is saved under. */
  key: string;
  eyebrow: string;
  title: string;
  options: string[];
};

/** A free-text URL step (e.g. an example client / company website). */
export type UrlStep = {
  type: "url";
  key: string;
  eyebrow: string;
  title: string;
  description?: string;
  placeholder: string;
};

export type FormStep = ChoiceStep | UrlStep;

/**
 * Contact resolved from the Calendly booking. Null means the API lookup failed,
 * so the contact step starts empty and the visitor fills it in.
 */
export type LeadContact = {
  email: string;
  firstName: string;
  lastName: string;
  website: string | null;
};

type ContactFields = {
  firstName: string;
  lastName: string;
  email: string;
  website: string;
};

const EMPTY_FIELDS: ContactFields = { firstName: "", lastName: "", email: "", website: "" };

/** Annual-revenue brackets shared by every schedule form. */
export const REVENUE_OPTIONS = [
  "Under $300k ARR (Less than $25k/mo)",
  "$300k - $1M ARR ($25k - $83k/mo)",
  "$1M - $3M ARR ($83k - $250k/mo)",
  "$3M - $10M ARR ($250k - $830k/mo)",
  "$10M+ ARR ($830k+/mo)",
];

const PRIMARY_BTN =
  "btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 mt-6 w-full text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60";

/**
 * Accepts a bare domain ("website.com") and returns a full URL. Visitors rarely
 * type the scheme, so we add it rather than reject the input.
 */
export function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * `type="url"` would force the visitor to type "https://". A text input with a
 * loose domain pattern keeps native validation without demanding the scheme.
 */
export const URL_INPUT_PROPS = {
  type: "text",
  inputMode: "url",
  autoCapitalize: "none",
  spellCheck: false,
  // `pattern` is compiled with the RegExp `v` flag, which requires `/` to be
  // escaped inside a character class — an invalid pattern is silently ignored,
  // disabling validation entirely. It also has no case-insensitive flag, hence
  // the scheme char classes.
  pattern: String.raw`([Hh][Tt][Tt][Pp][Ss]?:\/\/)?[^\s\/]+\.[^\s\/]{2,}(\/.*)?`,
  title: "Enter a website such as example.com",
} as const;

function OptionCard({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "w-full text-left rounded-xl border px-4 py-3.5 text-sm transition-all flex items-center justify-between gap-3",
        selected
          ? "border-pink-soft/60 bg-pink-soft/10 text-white"
          : "border-white/12 bg-white/[0.03] text-white/80 hover:border-white/25 hover:bg-white/[0.06]",
      ].join(" ")}
    >
      <span>{label}</span>
      {selected && <Check className="size-4 shrink-0 text-pink-soft" />}
    </button>
  );
}

function StepHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-5">
      <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-pink-soft/90 mb-2">
        {eyebrow}
      </div>
      <DialogTitle className="text-xl font-semibold tracking-tight text-white text-balance">
        {title}
      </DialogTitle>
    </div>
  );
}

function BackRow({
  onBack,
  submitting,
  inline = false,
}: {
  onBack: () => void;
  submitting: boolean;
  inline?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onBack}
      disabled={submitting}
      className={[
        "text-xs text-white/50 hover:text-white/80 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50",
        inline ? "mt-4 w-full justify-center" : "mt-5",
      ].join(" ")}
    >
      <ArrowLeft className="size-3.5" />
      Back
    </button>
  );
}

/**
 * Post-booking qualification wizard. Booking already happened in Calendly, so
 * this no longer opens Calendly — it confirms the booker's contact details
 * (prefilled from the booking) and progressively saves qualifying answers to
 * Loops, keyed by their email.
 */
export function ScheduleLeadForm({
  open,
  onOpenChange,
  formName,
  steps,
  contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Recorded to Loops so leads can be attributed to this form. */
  formName: string;
  /** Qualifying steps shown after the fixed contact step. */
  steps: FormStep[];
  /** The booker, resolved from Calendly; null when the API lookup failed. */
  contact: LeadContact | null;
}) {
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState<ContactFields>(EMPTY_FIELDS);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = steps.length + 1;
  const setField = (patch: Partial<ContactFields>) => setFields((f) => ({ ...f, ...patch }));

  /**
   * `contact` arrives async after booking, while the dialog is still closed, so
   * prefill has to sync on prop change / reopen — a `useState` initializer would
   * only ever capture the null it mounted with. Editing is preserved: `contact`
   * and `open` are stable while the wizard is open, so this never fires mid-edit.
   */
  useEffect(() => {
    setFields(
      contact
        ? {
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            website: contact.website ?? "",
          }
        : EMPTY_FIELDS,
    );
  }, [contact, open]);

  /** The email every Loops save keys to. */
  function currentEmail(): string {
    return fields.email.trim();
  }

  /**
   * Answers to single-select steps only. URL/free-text steps hold lead data
   * (company websites) that belongs in Loops, not in analytics event props —
   * `session_recording.maskAllInputs` masks the replay, not custom properties.
   */
  function choiceAnswers(): Record<string, string> {
    const picked: Record<string, string> = {};
    for (const s of steps) {
      if (s.type === "choice" && answers[s.key] !== undefined) picked[s.key] = answers[s.key];
    }
    return picked;
  }

  /** Fires once each time the form opens. */
  useEffect(() => {
    if (open) trackFormStart(formName);
  }, [open, formName]);

  /** Best-effort progressive save to Loops. Never blocks the wizard. */
  async function save(patch: Partial<LoopsContactInput>) {
    const email = currentEmail();
    if (!email) return;
    setSubmitting(true);
    try {
      await upsertLoopsContact({ data: { email, formName, ...patch } });
    } catch {
      // Lead capture is best-effort; the visitor should still reach the end.
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setStep(0);
    setAnswers({});
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  /** Ends the wizard: record the submission, then close. */
  function finish() {
    trackFormSubmit(formName, choiceAnswers());
    handleOpenChange(false);
  }

  async function onContactSubmit(e: React.FormEvent) {
    e.preventDefault();
    await save({
      firstName: fields.firstName,
      lastName: fields.lastName,
      companyWebsite: normalizeUrl(fields.website),
    });
    setStep(1);
  }

  /** Choice selected: save it, then advance — unless it's the final step. */
  async function onChoose(choice: ChoiceStep, opt: string, last: boolean) {
    setAnswers((a) => ({ ...a, [choice.key]: opt }));
    trackFormStep(formName, { step_key: choice.key, step_index: step - 1, value: opt });
    await save({ properties: { [choice.key]: opt } });
    if (!last) setStep((s) => s + 1);
  }

  async function onFinishFromChoice(choice: ChoiceStep) {
    await save({ properties: { [choice.key]: answers[choice.key] ?? "" } });
    finish();
  }

  async function onUrlSubmit(url: UrlStep, last: boolean, e: React.FormEvent) {
    e.preventDefault();
    const value = normalizeUrl(answers[url.key] ?? "");
    // No `value`: what the visitor typed is lead data, not an analytics dimension.
    trackFormStep(formName, { step_key: url.key, step_index: step - 1 });
    await save({ properties: { [url.key]: value } });
    if (last) {
      finish();
    } else {
      setStep((s) => s + 1);
    }
  }

  const cfg = step > 0 ? steps[step - 1] : null;
  const isLast = step === steps.length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-neutral-950 p-0 [&>button]:text-white/60 [&>button]:hover:text-white">
        <div className="p-6 md:p-8">
          {/* Progress — pr on mobile keeps the dots clear of the dialog close button */}
          <div className="mb-6 pr-8 md:pr-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                Step {step + 1} of {totalSteps}
              </span>
              <div className="flex gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <span
                    key={i}
                    className={[
                      "h-1.5 rounded-full transition-all",
                      i <= step ? "w-6 bg-pink-soft" : "w-3 bg-white/15",
                    ].join(" ")}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* STEP 1 — confirm contact details (prefilled from the booking) */}
          {step === 0 && (
            <form onSubmit={onContactSubmit}>
              <StepHeading
                eyebrow="You're booked"
                title="Confirm your details so we can prepare your AI Visibility Audit."
              />
              <DialogDescription className="sr-only">
                Confirm the contact details from your booking before the call.
              </DialogDescription>
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    required
                    placeholder="First name"
                    autoComplete="given-name"
                    value={fields.firstName}
                    onChange={(e) => setField({ firstName: e.target.value })}
                    className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  />
                  <Input
                    required
                    placeholder="Last name"
                    autoComplete="family-name"
                    value={fields.lastName}
                    onChange={(e) => setField({ lastName: e.target.value })}
                    className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                  />
                </div>
                <Input
                  required
                  type="email"
                  placeholder="Work email"
                  autoComplete="email"
                  value={fields.email}
                  onChange={(e) => setField({ email: e.target.value })}
                  className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                />
                <Input
                  required
                  {...URL_INPUT_PROPS}
                  placeholder="Company website (example.com)"
                  autoComplete="url"
                  value={fields.website}
                  onChange={(e) => setField({ website: e.target.value })}
                  className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40"
                />
              </div>
              <button type="submit" disabled={submitting} className={PRIMARY_BTN}>
                {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                Continue
                {!submitting && <ArrowRight className="size-4" />}
              </button>
            </form>
          )}

          {/* CHOICE step */}
          {cfg?.type === "choice" && (
            <div>
              <StepHeading eyebrow={cfg.eyebrow} title={cfg.title} />
              <div className="flex flex-col gap-2.5">
                {cfg.options.map((opt) => (
                  <OptionCard
                    key={opt}
                    label={opt}
                    selected={answers[cfg.key] === opt}
                    onSelect={() => onChoose(cfg, opt, isLast)}
                  />
                ))}
              </div>
              {isLast && (
                <button
                  type="button"
                  disabled={submitting || !answers[cfg.key]}
                  onClick={() => onFinishFromChoice(cfg)}
                  className={PRIMARY_BTN}
                >
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                  Done
                  {!submitting && <ArrowRight className="size-4" />}
                </button>
              )}
              <BackRow onBack={() => setStep(step - 1)} submitting={submitting} inline={isLast} />
            </div>
          )}

          {/* URL step */}
          {cfg?.type === "url" && (
            <form onSubmit={(e) => onUrlSubmit(cfg, isLast, e)}>
              <StepHeading eyebrow={cfg.eyebrow} title={cfg.title} />
              {cfg.description && (
                <p className="text-sm text-white/60 mb-4 text-pretty">{cfg.description}</p>
              )}
              <Input
                required
                {...URL_INPUT_PROPS}
                placeholder={cfg.placeholder}
                value={answers[cfg.key] ?? ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [cfg.key]: e.target.value }))}
                className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40"
              />
              <button type="submit" disabled={submitting} className={PRIMARY_BTN}>
                {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                {isLast ? "Done" : "Continue"}
                {!submitting && <ArrowRight className="size-4" />}
              </button>
              <BackRow onBack={() => setStep(step - 1)} submitting={submitting} inline />
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
